/* eslint-disable react/jsx-curly-newline */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { getDeviceResource } from 'apps/web_forms/api/api';
import { COLORS, ACCEPTED_TYPES_FOR_GENERATED_PARAM } from 'apps/web_forms/utils/constants';
import { addImageToSubmit, removeImageDraftValue, setCurrentSelectedMultimediaInput } from 'apps/web_forms/slices/multimediaSlice';
import RedoIcon from 'apps/web_forms/assets/redo.svg';
import UndoIcon from 'apps/web_forms/assets/undo.svg';
import TrashIcon from 'apps/web_forms/assets/trash.svg';
import ColorBucket from 'apps/web_forms/assets/color-bucket.svg';
import EditPencil from 'apps/web_forms/assets/edit-pencil.svg';
import AddImage from 'apps/web_forms/assets/camera-black-small.svg';
import Loader from 'react-loader-spinner';
import useLocalImage from 'apps/web_forms/hooks/useLocalImage';
import SignatureColorPicker from './SignatureColorPicker';
import SignatureStrokePicker from './SignatureStrokePicker';
import SignaturePad from './SignaturePad';
import useGlobalSelector from 'apps/web_forms/hooks/useGlobalSelector';

const MAX_SIGNATURE_WIDTH = '31em';

const StyledSignaturePadContainer = styled.div`
  border: 1px solid ${COLORS.grey};
  position: relative;
  width: 100%;
  max-width: ${MAX_SIGNATURE_WIDTH};
  button {
    position: absolute;
    background-color: transparent;
    border: none;
    right: 0.3em;
  }
  .save-btn {
    border-radius: 1.5em;
    background-color: ${COLORS.green};
    top: 0.5em;
    right: 0.3em;
    color: white;
    padding: 0.7em 1.2em 0.6em 1.2em;
    font-size: 0.8em;
  }
  .undo-btn {
    top: 2.8em;
  }
  .redo-btn {
    top: 5.5em;
  }
  .trash-btn {
    top: 8.3em;
  }
  canvas {
    width: 100%;
    height: 16em;
    max-width: ${MAX_SIGNATURE_WIDTH};
  }
`;

const StyledSignaturePadButtons = styled.div`
  margin-top: 1em;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  max-width: ${MAX_SIGNATURE_WIDTH};

  div {
    margin-right: 1em;
    margin-top: 1em;
    display: flex;
    justify-content: center;
    align-items: center;

    span {
      margin-left: 0.5em;
      font-size: 0.8em;
    }
  }

  button {
    height: 1.8em;
    width: 1.8em;
    background-color: ${COLORS.inputGroupsBackgroundColor};
    border-radius: 0.5em;
    border: 2px solid ${COLORS.inputGroupsBackgroundColor};
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const INITIAL_SNAPSHOT_INDEX = -1;

const SignaturePadContainer = ({ element }) => {
  const canvas = useRef(null);
  const dispatch = useDispatch();
  const signatureContainer = useRef(null);
  const [canvasSnapshots, setCanvasSnapshots] = useState([]);
  const [currentSnapshotIndex, setCurrentSnapshotIndex] = useState(
    INITIAL_SNAPSHOT_INDEX
  );
  const [imageLoading, setImageLoading] = useState(false);

  const { resources, resourceVersions } = useGlobalSelector();

  const [canvasOptions, setCanvasOptions] = useState({
    openColor: false,
    openStroke: false,
    activeColor: 'black',
    strokeSize: 2,
    drawType: 'free',
  });

  const handleOptionsClick = useCallback((options) => {
    setCanvasOptions((current) => {
      return { ...current, ...options };
    });
  }, []);

  const { handleFiles, currentFile, cleanImageData } = useLocalImage({
    onReupload: () => {},
  });

  useEffect(() => {
    return () =>
      setCanvasOptions({
        openColor: false,
        openStroke: false,
        activeColor: 'black',
        strokeSize: 2,
        drawType: 'free',
      });
  }, [element]);

  useEffect(() => {
    if (currentFile) {
      const nextIndex = currentSnapshotIndex + 1;
      setCanvasSnapshots([...canvasSnapshots.slice(0, nextIndex), currentFile]);
      setCurrentSnapshotIndex(nextIndex);
    }
  }, [currentFile]);

  const selectFileRef = useRef(null);

  const getClientWith = () => {
    if (!canvas.current || !canvas.current.getCanvas()) return null;
    return canvas.current.getCanvas().clientWidth;
  };

  const getClientHeight = () => {
    if (!canvas.current || !canvas.current.getCanvas()) return null;
    return canvas.current.getCanvas().clientHeight;
  };

  const loadBackgroundImage = useCallback(async () => {
    const backgroundResource = resources.find(
      (resource) => resource.identifier === element.background_resource
    );

    if (backgroundResource) {
      setImageLoading(true);
      const resourceId = backgroundResource.id;
      const version = resourceVersions?.[resourceId];
      const generated = ACCEPTED_TYPES_FOR_GENERATED_PARAM.includes(backgroundResource.mime_type); 
      const imageArrayBuffer = await getDeviceResource({resourceId, generated, version});
      setImageLoading(false);
      const imageSrc = URL.createObjectURL(
        // eslint-disable-next-line no-undef
        new Blob([imageArrayBuffer], {
          type: backgroundResource.mime_type,
        })
      );

      return imageSrc;
    }
    return null;
  }, [element.background_resource]);

  useEffect(
    function initNewCanvas() {
      async function loadFirstSnapshot() {
        let imageUrl = null;

        if (element.background_resource) {
          imageUrl = await loadBackgroundImage();
        }

        if (imageUrl) {
          setCanvasSnapshots([imageUrl]);
        } else {
          canvas.current.clear();
          setCanvasSnapshots([canvas.current.toDataURL()]);
        }

        setCurrentSnapshotIndex(0);
      }

      loadFirstSnapshot();
    },
    [element]
  );

  useEffect(
    function onSnapshotChange() {
      const cxt = canvas.current.getCanvas().getContext('2d');
      const image = new Image();
      image.src = canvasSnapshots[currentSnapshotIndex];
      image.onload = () => {
        canvas.current.clear();
        cxt.drawImage(image, 0, 0, getClientWith(), getClientHeight());
      };
    },
    [canvasSnapshots[currentSnapshotIndex]]
  );

  const save = () => {
    dispatch(
      addImageToSubmit({
        key: element.identifier,
        value: canvas.current.toDataURL('image/png'),
      })
    );
    dispatch(
      removeImageDraftValue(element.identifier)
    );
    dispatch(setCurrentSelectedMultimediaInput({}));
  };

  const undo = useCallback(() => {
    if (currentSnapshotIndex === 0) return;

    const currentIndex =
      currentSnapshotIndex < 1 ? 0 : currentSnapshotIndex - 1;
    setCurrentSnapshotIndex(currentIndex);
  }, [currentSnapshotIndex, canvasSnapshots]);

  const redo = useCallback(() => {
    const currentIndex =
      currentSnapshotIndex > canvasSnapshots.length - 2
        ? currentSnapshotIndex
        : currentSnapshotIndex + 1;
    setCurrentSnapshotIndex(currentIndex);
  }, [currentSnapshotIndex, canvasSnapshots]);

  const trash = useCallback(() => {
    setCanvasSnapshots(([firstSnapshot]) => [firstSnapshot]);
    setCurrentSnapshotIndex(0);
    cleanImageData();
  }, []);

  const newDrawSnapshot = useCallback(() => {
    const nextIndex = currentSnapshotIndex + 1;
    setCanvasSnapshots([
      ...canvasSnapshots.slice(0, nextIndex),
      canvas.current.toDataURL(),
    ]);

    setCurrentSnapshotIndex(nextIndex);
  }, [currentSnapshotIndex]);

  return (
    <>
      <StyledSignaturePadContainer ref={signatureContainer}>
        {imageLoading && (
          <Loader
            type="Oval"
            color={COLORS.primary}
            height="16em"
            width={100}
            timeout={3000}
          />
        )}

        {!imageLoading && (
          <>
            <SignaturePad
              activeColor={canvasOptions.activeColor}
              newDrawSnapshotHandler={newDrawSnapshot}
              strokeSize={canvasOptions.strokeSize}
              canvasRef={canvas}
              drawType={canvasOptions.drawType}
            />
            <button type="button" onClick={save} className="save-btn">
              Save
            </button>
            <button type="button" onClick={undo} className="undo-btn">
              <UndoIcon />
            </button>
            <button type="button" onClick={redo} className="redo-btn">
              <RedoIcon />
            </button>
            <button type="button" onClick={trash} className="trash-btn">
              <TrashIcon />
            </button>

            <SignatureColorPicker
              open={canvasOptions.openColor}
              clickHandler={handleOptionsClick}
            />

            <SignatureStrokePicker
              open={canvasOptions.openStroke}
              clickHandler={handleOptionsClick}
            />
          </>
        )}
      </StyledSignaturePadContainer>

      {element.type === 'sketch' && (
        <StyledSignaturePadButtons>
          <div>
            <button
              type="button"
              onClick={() =>
                setCanvasOptions({
                  ...canvasOptions,
                  openColor: !canvasOptions.openColor,
                  openStroke: false,
                })
              }
            >
              <ColorBucket />
            </button>

            <span>Select color</span>
          </div>

          <div>
            <button
              type="button"
              onClick={() =>
                setCanvasOptions({
                  ...canvasOptions,
                  openStroke: !canvasOptions.openStroke,
                  openColor: false,
                })
              }
            >
              <EditPencil />
            </button>

            <span>Select Stroke</span>
          </div>

          <div>
            <button type="button" onClick={() => selectFileRef.current.click()}>
              <AddImage />
            </button>

            <input
              type="file"
              accept="image/*"
              onChange={({ target }) => handleFiles(target.files)}
              ref={selectFileRef}
              style={{ display: 'none' }}
            />

            <span>Add Image</span>
          </div>
        </StyledSignaturePadButtons>
      )}
    </>
  );
};

SignaturePadContainer.propTypes = {
  element: PropTypes.shape({
    background_resource: PropTypes.string,
    identifier: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
};

export default SignaturePadContainer;
